/**
 * Parse an OSC 7 payload (`file://<host>/<path>`) into a local path.
 *
 * Our prompts (src-tauri/src/terminal.rs) send:
 *   PowerShell  file://localhost/C:\dev\app        (ProviderPath, backslashes)
 *   cmd         file://localhost/C:\dev\app        ($P)
 *   Git Bash    file://localhost/c/dev/app         ($PWD, MSYS form)
 *   unix shells file://<host>/home/me/app          (percent-encoded by some)
 * On Windows the result must be something `terminal_create` accepts as a
 * cwd (an existing directory), so the MSYS `/c/dev` form becomes `C:\dev`.
 *
 * SECURITY: OSC 7 can come from ANY output — `cat file`, an agent, a remote
 * log — not just our prompt. The result becomes the cwd of the next split,
 * a saved layout and an agent launch, and on Windows touching a UNC path
 * (`\\host\share`) leaks the user's NTLM hash over SMB. So:
 *   - the host must be empty, `localhost`, or `hostname` (this machine, when
 *     the caller knows it) — anything else is another machine's path;
 *   - a Windows result starting with `\\` or `//` (UNC, `\\?\`, `\\.\`) is
 *     refused. Rust refuses those again before any filesystem call
 *     (src-tauri/src/terminal/cwd.rs).
 * Returns null whenever the payload is not a safe local path.
 */
export function parseOsc7(data, platform = 'win', hostname = null) {
  if (typeof data !== 'string' || !data.startsWith('file://')) return null;
  const rest = data.slice('file://'.length);
  const slash = rest.indexOf('/');
  if (slash === -1) return null;
  const host = rest.slice(0, slash).toLowerCase();
  const local = host === '' || host === 'localhost' || (!!hostname && host === String(hostname).toLowerCase());
  if (!local) return null;
  let path = rest.slice(slash); // keep the leading "/"
  try {
    path = decodeURIComponent(path);
  } catch {
    // a literal "%" in a Windows path is fine as it is
  }
  if (platform !== 'win') return path && !path.startsWith('//') ? path : null;
  const drive = /^\/([A-Za-z]):?([\\/].*)?$/.exec(path);
  let out;
  if (drive && (path[2] === ':' || path[2] === '/' || path.length === 2)) {
    const tail = (drive[2] || '\\').replace(/\//g, '\\');
    out = `${drive[1].toUpperCase()}:${tail}`;
  } else {
    out = path.replace(/\//g, '\\');
  }
  if (!out || /^[\\/]{2}/.test(out)) return null;
  return out;
}
